"use client"

import React, { useState, useEffect } from "react"
import { Card, CardBody, CardHeader, Row, Col, Badge } from "reactstrap"
import { FaEye, FaSearch, FaCalendar } from "react-icons/fa"
import DataTable, { type TableColumn } from "react-data-table-component"
import { Btn } from "../../../AbstractElements"
import { BadgeVerificationService } from "../../../Service/BadgeEntryVerificationService"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import type { BadgeEntryVerification, PaginatedResponse } from "./types"
import { toast } from "react-toastify"

interface Props {
  onViewDetails: (verification: BadgeEntryVerification) => void
  refreshTrigger: boolean
}

const BadgeEntryVerificationList: React.FC<Props> = ({ onViewDetails, refreshTrigger }) => {
  const [verifications, setVerifications] = useState<BadgeEntryVerification[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [dateFrom, setDateFrom] = useState<Date | null>(null)
  const [dateTo, setDateTo] = useState<Date | null>(null)
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    pageSize: 10,
  })

  const formatDateOnly = (date: Date | null) => {
    if (!date) return ""
    const year = date.getFullYear()
    const month = (date.getMonth() + 1).toString().padStart(2, "0")
    const day = date.getDate().toString().padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  const fetchVerifications = async (
    page = 1,
    searchTerm = "",
    fromDate: Date | null = null,
    toDate: Date | null = null,
  ) => {
    try {
      setLoading(true)
      const response: PaginatedResponse<BadgeEntryVerification> = await BadgeVerificationService.getAll(
        page,
        pagination.pageSize,
        searchTerm,
        formatDateOnly(fromDate),
        formatDateOnly(toDate),
      )
      setVerifications(response.results)
      setPagination({
        currentPage: response.current_page,
        totalPages: response.total_pages,
        totalCount: response.count,
        pageSize: response.page_size,
      })
    } catch (error) {
      console.error("Failed to fetch badge verifications:", error)
      toast.error("Failed to load badge verifications")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchVerifications()
  }, [refreshTrigger])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    fetchVerifications(1, search, dateFrom, dateTo)
  }

  const handlePageChange = (page: number) => {
    fetchVerifications(page, search, dateFrom, dateTo)
  }

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPagination((prev) => ({ ...prev, pageSize: newPerPage }))
    fetchVerifications(page, search, dateFrom, dateTo)
  }

  const columns: TableColumn<BadgeEntryVerification>[] = [
    {
      name: "Date & Time",
      selector: (row) => row.created_at,
      sortable: true,
      cell: (row) => (
        <div>
          <div>{new Date(row.created_at).toLocaleDateString()}</div>
          <small className="text-muted">{new Date(row.created_at).toLocaleTimeString()}</small>
        </div>
      ),
    },
    {
      name: "Badge Owner",
      selector: (row) => row.badge_owner_name,
      sortable: true,
      cell: (row) => (
        <div>
          <strong>{row.badge_owner_name}</strong>
          {row.badge_owner_email && <div className="text-muted small">{row.badge_owner_email}</div>}
        </div>
      ),
    },
    {
      name: "Exhibitor",
      selector: (row) => row.exhibitor_name,
      sortable: true,
      cell: (row) => (
        <span >
          {row.exhibitor_name}
        </span>
      ),
    },
    {
      name: "Exhibition",
      selector: (row) => row.exhibition_title,
      sortable: true,
      cell: (row) => (
        <div>
          <div>{row.exhibition_title}</div>
          <small className="text-muted">{row.exhibition_dates}</small>
        </div>
      ),
    },
    {
      name: "Turnstile Gate",
      selector: (row) => row.turnstile_number,
      sortable: true,
      cell: (row) => <Badge color="primary">{row.turnstile_number}</Badge>,
    },
    {
      name: "Helper",
      selector: (row) => row.helper_name,
      sortable: true,
      cell: (row) => (
        <div>
          <div>{row.helper_name}</div>
          {row.helper_email && <small className="text-muted">{row.helper_email}</small>}
        </div>
      ),
    },
    {
      name: "Actions",
      cell: (row) => (
        <Btn color="info" size="sm" onClick={() => onViewDetails(row)} title="View Details">
          <FaEye />
        </Btn>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ]

  return (
    <Card>
      <CardHeader>
        <Row className="align-items-center">
          <Col md="6">
            <h5 className="mb-0">Badge Entries Verifications ({pagination.totalCount})</h5>
          </Col>
        </Row>
      </CardHeader>
      <CardBody>
        {/* Search and Filters */}
        <form onSubmit={handleSearch} className="mb-3">
          <Row>
            <Col md="4">
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search verifications..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <button className="btn btn-outline-secondary btn-sm" type="submit">
                  <FaSearch />
                </button>
              </div>
            </Col>
            <Col md="3">
              <DatePicker
                selected={dateFrom}
                onChange={(date) => setDateFrom(date)}
                placeholderText="From Date"
                className="form-control"
                dateFormat="yyyy-MM-dd"
              />
            </Col>
            <Col md="3">
              <DatePicker
                selected={dateTo}
                onChange={(date) => setDateTo(date)}
                placeholderText="To Date"
                className="form-control"
                dateFormat="yyyy-MM-dd"
              />
            </Col>
            <Col md="2">
              <Btn
                color="secondary"
                size="sm"
                onClick={() => fetchVerifications(1, search, dateFrom, dateTo)}
              >
                <FaCalendar className="me-1" />
                Filter
              </Btn>
            </Col>
          </Row>
        </form>

        {/* DataTable */}
        <DataTable
          columns={columns}
          data={verifications}
          progressPending={loading}
          pagination
          paginationServer
          paginationTotalRows={pagination.totalCount}
          paginationDefaultPage={pagination.currentPage}
          paginationPerPage={pagination.pageSize}
          paginationRowsPerPageOptions={[10, 20, 30, 50]}
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          noDataComponent="No badge entries verifications found"
          striped
          highlightOnHover
          responsive
        />
      </CardBody>
    </Card>
  )
}

export default BadgeEntryVerificationList
